import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import TeacherSidebar from "../components/TeacherSidebar";
import MobileBottomNav from "../components/MobileBottomNav";
import MessageCenter from "../components/MessageCenter";
import StudentHeader from "../components/StudentHeader";
import {
  BookOpen,
  Loader2,
  Search,
  Users,
  ClipboardList,
  ChevronRight,
  GraduationCap,
  BarChart3,
  TrendingUp,
  Calendar,
  Star,
  Layers,
  FileText,
  Filter,
  X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeacherCoursesPage({ user, token, onLogout }) {
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [settings, setSettings] = useState(null);
  const [filtroNivel, setFiltroNivel] = useState("");
  const [filtroGrado, setFiltroGrado] = useState("todos");
  const [filtroSeccion, setFiltroSeccion] = useState("todos");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const currentSubdomain = subdomain || user?.subdomain || 'elroble';
      const [coursesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/teacher/courses`, { headers }),
        axios.get(`${API}/api/settings/public/${currentSubdomain}`).catch(() => ({ data: null }))
      ]);
      setCourses(coursesRes.data.courses || []);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error("Error loading courses:", err);
    } finally {
      setLoading(false);
    }
  };

  const schoolName = settings?.system_name || user?.school_name || "Portal Docente";

  const navigateTo = (path) => {
    if (subdomain) {
      navigate(`/${subdomain}${path}`);
    } else {
      navigate(path);
    }
  };

  const nivelSeleccionado = filtroNivel !== "";

  const nivelesDisponibles = useMemo(() =>
    [...new Set(courses.map(c => c.level_name).filter(Boolean))].sort()
  , [courses]);

  const gradosDisponibles = useMemo(() => {
    if (!nivelSeleccionado) return [];
    return [...new Set(courses.filter(c => c.level_name === filtroNivel).map(c => c.grade_name).filter(Boolean))].sort();
  }, [courses, filtroNivel, nivelSeleccionado]);

  const seccionesDisponibles = useMemo(() => {
    if (!nivelSeleccionado || filtroGrado === "todos") return [];
    return [...new Set(courses.filter(c => c.level_name === filtroNivel && c.grade_name === filtroGrado).map(c => c.section_name).filter(Boolean))].sort();
  }, [courses, filtroNivel, filtroGrado, nivelSeleccionado]);

  const filteredCourses = useMemo(() => {
    if (!nivelSeleccionado) return [];
    return courses.filter(c => {
      if (c.level_name !== filtroNivel) return false;
      if (filtroGrado !== "todos" && c.grade_name !== filtroGrado) return false;
      if (filtroSeccion !== "todos" && c.section_name !== filtroSeccion) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!c.name?.toLowerCase().includes(q) && !c.section_name?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [courses, filtroNivel, filtroGrado, filtroSeccion, searchQuery, nivelSeleccionado]);

  const handleNivelChange = (val) => {
    setFiltroNivel(val);
    setFiltroGrado("todos");
    setFiltroSeccion("todos");
  };

  const handleGradoChange = (val) => {
    setFiltroGrado(val);
    setFiltroSeccion("todos");
  };

  const handleLimpiar = () => {
    setFiltroNivel("");
    setFiltroGrado("todos");
    setFiltroSeccion("todos");
    setSearchQuery("");
  };

  // Calculate stats
  const totalStudents = courses.reduce((sum, c) => sum + (c.students_count || 0), 0);
  const totalTasks = courses.reduce((sum, c) => sum + (c.tasks_count || 0), 0);
  const totalMaterials = courses.reduce((sum, c) => sum + (c.materials_count || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex" data-testid="teacher-courses-page">
      <TeacherSidebar
        active="cursos"
        onNavigate={() => {}}
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded(!sidebarExpanded)}
        onLogout={onLogout}
        schoolName={schoolName}
        subdomain={subdomain || user?.subdomain}
        user={user}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <StudentHeader
          user={user}
          onMenuClick={() => setSidebarExpanded(!sidebarExpanded)}
          onLogout={onLogout}
          logoUrl={settings?.logo_url}
          schoolName={schoolName}
          subdomain={subdomain || user?.subdomain}
          token={token}
          roleLabel="Docente"
          profilePath="/teacher/profile"
        />

        <main className="flex-1 p-3 sm:p-4 lg:p-8 pb-20 lg:pb-8 overflow-y-auto">
          {/* Hero Section */}
          <div className="relative mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-8 text-white shadow-2xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
              <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-white rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2" />
            </div>
            
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <GraduationCap className="w-7 h-7" />
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold">Mis Cursos</h1>
                    <p className="text-white/80 text-sm">Gestiona tus asignaturas</p>
                  </div>
                </div>
                <p className="text-white/90 max-w-xl">
                  Accede a tus cursos, revisa el progreso de tus estudiantes y administra el contenido académico.
                </p>
              </div>
              
              {/* Quick Stats in Hero */}
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-4 text-center border border-white/20 min-w-[100px]">
                  <p className="text-3xl font-bold">{courses.length}</p>
                  <p className="text-white/80 text-xs mt-1">Cursos</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-4 text-center border border-white/20 min-w-[100px]">
                  <p className="text-3xl font-bold">{totalStudents}</p>
                  <p className="text-white/80 text-xs mt-1">Estudiantes</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-4 text-center border border-white/20 min-w-[100px]">
                  <p className="text-3xl font-bold">{totalTasks}</p>
                  <p className="text-white/80 text-xs mt-1">Tareas</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{courses.length}</p>
                  <p className="text-sm text-slate-500">Cursos Activos</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{totalStudents}</p>
                  <p className="text-sm text-slate-500">Total Estudiantes</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{totalTasks}</p>
                  <p className="text-sm text-slate-500">Tareas Creadas</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{totalMaterials}</p>
                  <p className="text-sm text-slate-500">Materiales</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-4 mb-6" data-testid="teacher-filter-bar">
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Nivel <span className="text-red-400">*</span></label>
                <select
                  value={filtroNivel}
                  onChange={(e) => handleNivelChange(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                  data-testid="filter-nivel"
                >
                  <option value="">Selecciona un nivel</option>
                  {nivelesDisponibles.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Grado</label>
                <select
                  value={filtroGrado}
                  onChange={(e) => handleGradoChange(e.target.value)}
                  disabled={!nivelSeleccionado}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!nivelSeleccionado ? "Selecciona un nivel primero" : ""}
                  data-testid="filter-grado"
                >
                  <option value="todos">Todos los grados</option>
                  {gradosDisponibles.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Sección</label>
                <select
                  value={filtroSeccion}
                  onChange={(e) => setFiltroSeccion(e.target.value)}
                  disabled={!nivelSeleccionado || filtroGrado === "todos"}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={filtroGrado === "todos" ? "Selecciona un grado primero" : ""}
                  data-testid="filter-seccion"
                >
                  <option value="todos">Todas las secciones</option>
                  {seccionesDisponibles.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {nivelSeleccionado && (
                <button
                  onClick={handleLimpiar}
                  className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors whitespace-nowrap"
                  data-testid="filter-limpiar"
                >
                  <X className="w-4 h-4" />
                  Limpiar
                </button>
              )}
            </div>
            {nivelSeleccionado && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500">Mostrando <span className="font-semibold text-slate-700">{filteredCourses.length}</span> curso{filteredCourses.length !== 1 ? "s" : ""}</p>
                <div className="relative max-w-[200px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar..."
                    className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                    data-testid="course-search-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Courses Grid or Empty State */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
              </div>
              <p className="text-slate-500">Cargando cursos...</p>
            </div>
          ) : !nivelSeleccionado ? (
            <div className="text-center py-16" data-testid="empty-state-select-nivel">
              <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-violet-500/10">
                <Filter className="w-10 h-10 text-violet-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-700 mb-2">Selecciona un nivel para ver tus cursos</h3>
              <p className="text-sm text-slate-400 max-w-sm mx-auto">Usa el filtro de arriba para elegir el nivel educativo y acceder a tus cursos asignados.</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-16" data-testid="empty-state-no-results">
              <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <BookOpen className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-700 mb-2">No hay cursos que coincidan</h3>
              <p className="text-sm text-slate-400 max-w-sm mx-auto mb-4">No se encontraron cursos con los filtros seleccionados.</p>
              <button onClick={handleLimpiar} className="px-4 py-2 text-sm text-violet-600 hover:bg-violet-50 rounded-xl transition-colors font-medium">Limpiar filtros</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredCourses.map((course, index) => (
                <div
                  key={`${course.id}-${course.section_id}`}
                  onClick={() => navigateTo(`/curso/${course.id}`)}
                  className="group bg-white rounded-3xl border border-slate-200/80 overflow-hidden hover:shadow-2xl hover:border-violet-300 transition-all duration-500 cursor-pointer hover:-translate-y-1"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`course-card-${course.id}`}
                >
                  {/* Course Image/Banner */}
                  <div className="relative h-44 overflow-hidden">
                    {course.image_url ? (
                      <>
                        <img 
                          src={course.image_url} 
                          alt={course.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      </>
                    ) : (
                      <div 
                        className="w-full h-full flex items-center justify-center relative"
                        style={{ background: `linear-gradient(135deg, ${course.color || '#8B5CF6'}, ${course.color ? adjustColor(course.color, -30) : '#6D28D9'})` }}
                      >
                        <div className="absolute inset-0 opacity-20">
                          <div className="absolute top-4 right-4 w-24 h-24 bg-white rounded-full blur-2xl" />
                          <div className="absolute bottom-4 left-4 w-16 h-16 bg-white rounded-full blur-xl" />
                        </div>
                        <BookOpen className="w-20 h-20 text-white/90 relative z-10" />
                      </div>
                    )}
                    
                    {/* Level Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-semibold text-slate-700 shadow-lg">
                        {course.level_name || "Curso"}
                      </span>
                    </div>
                    
                    {/* Course Title Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="font-bold text-white text-xl mb-1 line-clamp-2 drop-shadow-lg">
                        {course.name}
                      </h3>
                      <p className="text-white/90 text-sm">
                        {course.grade_name && `${course.grade_name} - `}{course.section_name || "Sin sección"}
                      </p>
                    </div>
                  </div>
                  
                  {/* Course Stats */}
                  <div className="p-5">
                    <div className="grid grid-cols-3 gap-3 mb-5">
                      <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
                        <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                        <p className="text-lg font-bold text-slate-800">{course.students_count || 0}</p>
                        <p className="text-xs text-slate-500">Alumnos</p>
                      </div>
                      <div className="text-center p-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl">
                        <ClipboardList className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                        <p className="text-lg font-bold text-slate-800">{course.tasks_count || 0}</p>
                        <p className="text-xs text-slate-500">Tareas</p>
                      </div>
                      <div className="text-center p-3 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
                        <FileText className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                        <p className="text-lg font-bold text-slate-800">{course.materials_count || 0}</p>
                        <p className="text-xs text-slate-500">Material</p>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    <button className="w-full py-3.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-lg shadow-violet-500/20 group-hover:shadow-violet-500/40">
                      <span>Gestionar Curso</span>
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      <MobileBottomNav role="teacher" />
      <MessageCenter token={token} user={user} />
    </div>
  );
}

// Helper function to adjust color brightness
function adjustColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (0x1000000 + 
    (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 + 
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 + 
    (B < 255 ? B < 1 ? 0 : B : 255)
  ).toString(16).slice(1);
}
